import {Injectable, Injector, inject} from '@angular/core';
import {MissingTranslationHandler, MissingTranslationHandlerParams, TranslateLoader} from '@ngx-translate/core';
import {catchError, map, of} from 'rxjs';
import {FeatureTranslateLoader, TranslationBundle} from './feature-translate.loader';
import {FeatureTranslationService} from './feature-translation.service';

export function bundleForTranslation(key: string): TranslationBundle | undefined {
    if (/^(audio|detector|image)\.teaser\.|^feature-teaser\./.test(key)) return 'teaser';
    if (key.startsWith('subscription.feature.')) return 'plans';
    if (key.startsWith('ai.detector.')) return 'detector';
    return (['audio', 'translator', 'paraphraser', 'presentations'] as const)
        .find(bundle => key.startsWith(`${bundle}.`));
}

/** Translate pipes in shared UI (e.g. an audio player inside a chat) request their own pack.
 * Imperative instant() consumers must await ensure() before creating feature UI. */
@Injectable()
export class FeatureTranslationHandler implements MissingTranslationHandler {
    private readonly injector = inject(Injector);

    handle(params: MissingTranslationHandlerParams) {
        const bundle = bundleForTranslation(params.key);
        const loader = this.injector.get(TranslateLoader);
        if (!bundle || !(loader instanceof FeatureTranslateLoader)) return params.key;
        const languages = [params.translateService.getCurrentLang(), params.translateService.getFallbackLang() || 'en'];
        if (languages.every(lang => lang && loader.hasBundle(lang, bundle))) return params.key;
        return this.injector.get(FeatureTranslationService).ensure(bundle).pipe(
            map(() => languages.some(lang => lang && loader.hasKey(lang, bundle, params.key))
                ? params.translateService.instant(params.key, params.interpolateParams) as string
                : params.key),
            catchError(() => of(params.key)),
        );
    }
}
